import Tool, { type ToolArgs } from '../../../tools/Tool';
import React, { useState, useRef } from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import styles from './EditLinkCard.module.css';
import toolbarStyles from '../../../components/Toolbar/Toolbar.module.css';
import classNames from 'classnames';
import { isLinkCardElement } from '../../../types/guards';
import useFocusElementToolbarInput from '../../../components/ElementToolbar/useFocusElementToolbarInput';
import type { Placement } from '@floating-ui/dom';

const EditLinkCardModal: React.FC<ToolArgs> = ({
  editor,
  element,
  toolbarHideModal,
}) => {
  const defaultValue =
    element && isLinkCardElement(element) ? element.linkCard_href ?? '' : '';
  const [originalValue] = useState(defaultValue);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useFocusElementToolbarInput(inputRef);

  const defaultLinkText =
    element && isLinkCardElement(element)
      ? element.linkCard_linkText ?? ''
      : '';
  const [originalLinkText] = useState(defaultLinkText);
  const [linkText, setLinkText] = useState(defaultLinkText);

  if (!element) return null;

  const updateLinkCardElement = (href: string, linkText: string) => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      {
        linkCard_href: href,
        linkCard_linkText: linkText,
      },
      {
        at: path,
      }
    );
  };

  const handleUrlKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkCardElement(value, linkText);
        ReactEditor.focus(editor);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkCardElement(originalValue, originalLinkText);
        ReactEditor.focus(editor);
        break;
    }
  };

  const handleUrlBlur = () => {
    if (value !== originalValue || linkText !== originalLinkText) {
      updateLinkCardElement(value, linkText);
    }
  };

  const handleLinkTextKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkCardElement(value, linkText);
        ReactEditor.focus(editor);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkCardElement(originalValue, originalLinkText);
        ReactEditor.focus(editor);
        break;
    }
  };

  const handleLinkTextBlur = () => {
    if (value !== originalValue || linkText !== originalLinkText) {
      updateLinkCardElement(value, linkText);
    }
  };

  const handleRemoveLink = () => {
    setValue('');
    setLinkText('');
    updateLinkCardElement('', '');
  };

  return (
    <div className={styles.editLink}>
      <label className={styles.label}>
        <div className={styles.labelName}>URL</div>
        <input
          ref={inputRef}
          type="text"
          className={classNames(
            'unstyled-input',
            toolbarStyles.toolbarInput,
            styles.input
          )}
          placeholder="URL"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleUrlKeyDown}
          onBlur={handleUrlBlur}
        />
      </label>

      <label className={styles.label}>
        <div className={styles.labelName}>Link text</div>
        <input
          type="text"
          className={classNames(
            'unstyled-input',
            toolbarStyles.toolbarInput,
            styles.input
          )}
          placeholder="Text"
          value={linkText}
          onChange={(event) => setLinkText(event.target.value)}
          onKeyDown={handleLinkTextKeyDown}
          onBlur={handleLinkTextBlur}
        />
      </label>

      {value && (
        <div>
          <aha-button kind="link" role="button" onClick={handleRemoveLink}>
            <span className={styles.buttonText}>
              <i className="fa-regular fa-link-simple-slash" /> Remove link
            </span>
          </aha-button>
        </div>
      )}
    </div>
  );
};

// Edit the link card and title.
class ToolEditLinkCardUrl extends Tool {
  static toolName = 'Edit link';
  static iconClass = 'fa-regular fa-link';
  static withoutRefocus = true;

  // Show the modal view to editor the URL.
  static action({ editor, element, toolbarShowModal }: ToolArgs): void {
    toolbarShowModal?.(ToolEditLinkCardUrl);
  }

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    return <i className="fa-regular fa-link-simple" />;
  }

  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return <EditLinkCardModal {...toolArgs} />;
  }

  static tooltipPlacement(): Placement {
    return 'bottom';
  }
}

export default ToolEditLinkCardUrl;
